import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

interface DemoOnlyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Generic "this is a demo" modal shown when a user attempts to create an
 * account. Replaces the signup flow site-wide.
 */
export function DemoOnlyModal({
  isOpen,
  onClose,
}: DemoOnlyModalProps): JSX.Element | null {
  const primaryRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) primaryRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;
  if (typeof document === "undefined" || !document.body) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[100000] bg-black/70 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        className="fixed inset-0 z-[100001] flex items-center justify-center p-4 sm:p-6"
        role="presentation"
        onClick={onClose}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-only-title"
          aria-describedby="demo-only-desc"
          className="w-full max-w-md rounded-2xl border border-[var(--accent)]/40 bg-[var(--bg-main)] p-6 shadow-2xl sm:p-8"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--accent)]/40 bg-[var(--accent)]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-[var(--accent)]">
            Heads up
          </div>
          <h2
            id="demo-only-title"
            className="text-xl font-bold text-[var(--text-main)] sm:text-2xl"
          >
            This is just a demo
          </h2>
          <p
            id="demo-only-desc"
            className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]"
          >
            Sign-in and sign-up are disabled in this demonstration. Feel free
            to keep exploring the site as a guest.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="min-h-[44px] rounded-lg border border-[var(--border-default)] bg-transparent px-5 text-sm font-medium text-[var(--text-main)] transition hover:bg-[var(--bg-elevated)]"
            >
              Close
            </button>
            <Link
              ref={primaryRef}
              to="/menu"
              onClick={onClose}
              className="btn-primary inline-flex min-h-[44px] items-center justify-center px-5 text-sm font-semibold"
            >
              Browse the menu
            </Link>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
